import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Star,
  Loader2,
  Calendar,
  Clock,
  DollarSign,
  Video,
  Filter,
  Users,
  Building2,
  X,
  User,
  Award,
  Sparkles,
} from 'lucide-react';
import { adminAPI, industriesAPI, speakersAPI } from '@/services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatET, formatETInputDate, formatETInputTime, etInputToISO } from '@/utils/dateUtils';
import { RichTextEditor } from '@/components/RichTextEditor';
import { SearchableSelect } from '@/components/common/SearchableSelect';

export default function AdminSeminars() {
  const [seminars, setSeminars] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'inactive'
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // all, live, recorded, certified
  const [industryFilter, setIndustryFilter] = useState('all');
  const [speakerFilter, setSpeakerFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingSeminar, setEditingSeminar] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // AI Content Improvement
  const [aiProcessing, setAiProcessing] = useState(false);
  const [contentFile, setContentFile] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    short_description: '',
    thumbnail_url: '',
    speaker_ids: [],
    industry_id: '',
    seminar_type: 'live',
    level: 'intermediate',
    status: 'draft',
    scheduled_date: '',
    scheduled_time: '',
    end_date: '',
    end_time: '',
    duration_hours: 4,
    is_multi_day: false,
    number_of_days: 1,
    timezone: 'America/New_York',
    // Pricing
    price_live: 0,
    price_recorded: 0,
    price_digital_download: 0,
    price_group_live: 0,
    // Content sections
    overview: '',
    areas_covered: [],
    why_attend: '',
    who_should_attend: '',
    topic_background: '',
    // Meta
    has_cpe_credits: false,
    cpe_credits: 0,
    is_certified: false,
    learning_objectives: [],
    tags: [],
    is_featured: false,
    allows_offline_request: true,
  });
  const [newArea, setNewArea] = useState('');
  const [newObjective, setNewObjective] = useState('');
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchData();
  }, [activeTab, statusFilter, typeFilter, industryFilter, speakerFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      
      // Handle Active vs Inactive tab logic
      if (activeTab === 'active') {
        if (statusFilter !== 'all') {
          params.status = statusFilter;
        } else {
          params.is_active_tab = true;
        }
      } else {
        params.status = 'disabled';
      }
      
      // Type filter (live/recorded/certified) - only for active tab
      if (activeTab === 'active' && typeFilter !== 'all') {
        if (typeFilter === 'certified') {
          params.has_cpe = true; // Filter by CPE certified
        } else {
          params.seminar_type = typeFilter; // 'live' or 'recorded'
        }
      }
      
      if (industryFilter !== 'all') params.industry_id = industryFilter;
      if (speakerFilter !== 'all') params.speaker_id = speakerFilter;
      
      const [seminarsRes, industriesRes, speakersRes] = await Promise.all([
        adminAPI.getSeminars(params),
        industriesAPI.getFlat(),
        // Include inactive speakers so admins can assign historical speakers
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/speakers-dropdown?include_inactive=true`).then(r => r.json()),
      ]);
      setSeminars(seminarsRes.data || []);
      setIndustries(industriesRes.data || []);
      setSpeakers(speakersRes || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load seminars');
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setStatusFilter('all');
    setTypeFilter('all');
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .trim();
  };

  const handleOpenForm = (seminar = null) => {
    if (seminar) {
      setEditingSeminar(seminar);
      const scheduledDate = seminar.scheduled_date ? new Date(seminar.scheduled_date) : null;
      const endDate = seminar.end_date ? new Date(seminar.end_date) : null;
      setFormData({
        title: seminar.title || '',
        slug: seminar.slug || '',
        description: seminar.description || '',
        short_description: seminar.short_description || '',
        thumbnail_url: seminar.thumbnail_url || '',
        speaker_ids: seminar.speaker_ids || [],
        industry_id: seminar.industry_id || '',
        seminar_type: seminar.seminar_type || 'live',
        level: seminar.level || 'intermediate',
        status: seminar.status || 'draft',
        scheduled_date: seminar.scheduled_date ? formatETInputDate(seminar.scheduled_date) : '',
        scheduled_time: seminar.scheduled_date ? formatETInputTime(seminar.scheduled_date) : '',
        end_date: seminar.end_date ? formatETInputDate(seminar.end_date) : '',
        end_time: seminar.end_date ? formatETInputTime(seminar.end_date) : '',
        duration_hours: seminar.duration_hours || 4,
        is_multi_day: seminar.is_multi_day || false,
        number_of_days: seminar.number_of_days || 1,
        timezone: seminar.timezone || 'America/New_York',
        price_live: seminar.price_live || seminar.price || 0,
        price_recorded: seminar.price_recorded || 0,
        price_digital_download: seminar.price_digital_download || 0,
        price_group_live: seminar.price_group_live || 0,
        overview: seminar.overview || '',
        areas_covered: seminar.areas_covered || [],
        why_attend: seminar.why_attend || '',
        who_should_attend: seminar.who_should_attend || '',
        topic_background: seminar.topic_background || '',
        has_cpe_credits: seminar.has_cpe_credits || false,
        cpe_credits: seminar.cpe_credits || 0,
        is_certified: seminar.is_certified || false,
        learning_objectives: seminar.learning_objectives || [],
        tags: seminar.tags || [],
        is_featured: seminar.is_featured || false,
        allows_offline_request: seminar.allows_offline_request !== false,
      });
    } else {
      setEditingSeminar(null);
      setFormData({
        title: '',
        slug: '',
        description: '',
        short_description: '',
        thumbnail_url: '',
        speaker_ids: [],
        industry_id: '',
        seminar_type: 'live',
        level: 'intermediate',
        status: 'draft',
        scheduled_date: '',
        scheduled_time: '',
        end_date: '',
        end_time: '',
        duration_hours: 4,
        is_multi_day: false,
        number_of_days: 1,
        timezone: 'America/New_York',
        price_live: 0,
        price_recorded: 0,
        price_digital_download: 0,
        price_group_live: 0,
        overview: '',
        areas_covered: [],
        why_attend: '',
        who_should_attend: '',
        topic_background: '',
        has_cpe_credits: false,
        cpe_credits: 0,
        is_certified: false,
        learning_objectives: [],
        tags: [],
        is_featured: false,
        allows_offline_request: true,
      });
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingSeminar(null);
  };

  const handleSaveSeminar = async () => {
    if (!formData.title || !formData.industry_id) {
      toast.error('Please fill in required fields (Title, Industry)');
      return;
    }

    try {
      setSaving(true);
      
      let scheduled_date = null;
      if (formData.scheduled_date && formData.scheduled_time) {
        scheduled_date = etInputToISO(formData.scheduled_date, formData.scheduled_time);
      }
      
      let end_date = null;
      if (formData.is_multi_day && formData.end_date) {
        end_date = etInputToISO(formData.end_date, formData.end_time || '17:00');
      }

      const data = {
        title: formData.title,
        slug: formData.slug || generateSlug(formData.title),
        description: formData.description,
        short_description: formData.short_description,
        thumbnail_url: formData.thumbnail_url,
        speaker_ids: formData.speaker_ids,
        industry_id: formData.industry_id,
        seminar_type: formData.seminar_type,
        level: formData.level,
        status: formData.status,
        scheduled_date,
        end_date,
        duration_hours: parseFloat(formData.duration_hours),
        is_multi_day: formData.is_multi_day,
        number_of_days: parseInt(formData.number_of_days),
        timezone: formData.timezone,
        price: parseFloat(formData.price_live),
        price_live: parseFloat(formData.price_live),
        price_recorded: parseFloat(formData.price_recorded),
        price_digital_download: parseFloat(formData.price_digital_download),
        price_group_live: parseFloat(formData.price_group_live),
        overview: formData.overview,
        areas_covered: formData.areas_covered,
        why_attend: formData.why_attend,
        who_should_attend: formData.who_should_attend,
        topic_background: formData.topic_background,
        has_cpe_credits: formData.has_cpe_credits,
        cpe_credits: formData.has_cpe_credits ? parseFloat(formData.cpe_credits) : 0,
        is_certified: formData.is_certified,
        learning_objectives: formData.learning_objectives,
        tags: formData.tags,
        is_featured: formData.is_featured,
        allows_offline_request: formData.allows_offline_request,
      };

      if (editingSeminar) {
        await adminAPI.updateSeminar(editingSeminar.id, data);
        toast.success('Seminar updated successfully');
      } else {
        const response = await adminAPI.createSeminar(data);
        toast.success(`Seminar created! Code: ${response.data.seminar_code}`);
      }
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save seminar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSeminar = async (seminar) => {
    if (!window.confirm(`Are you sure you want to delete "${seminar.title}"?`)) {
      return;
    }
    try {
      await adminAPI.deleteSeminar(seminar.id);
      toast.success('Seminar deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete seminar');
    }
  };

  // AI Content Improvement
  const handleAiImproveContent = async () => {
    if (!contentFile) {
      toast.error('Please select a PDF or Word document first');
      return;
    }

    setAiProcessing(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', contentFile);
      formDataUpload.append('content_type', 'seminar');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/improvise-content`, {
        method: 'POST',
        body: formDataUpload,
      });

      const result = await response.json();

      if (result.success && result.improvised_content) {
        const content = result.improvised_content;
        
        // Update form with AI-generated content
        setFormData(prev => ({
          ...prev,
          overview: content.overview || prev.overview,
          areas_covered: Array.isArray(content.areas_covered) 
            ? content.areas_covered 
            : prev.areas_covered,
          why_attend: content.why_should_attend || prev.why_attend,
          who_should_attend: content.who_should_attend || prev.who_should_attend,
          topic_background: content.topic_background || prev.topic_background,
          learning_objectives: content.learning_objectives 
            ? [content.learning_objectives] 
            : prev.learning_objectives,
        }));

        toast.success('Content improved with AI! Review and edit as needed.');
        setContentFile(null);
      } else {
        toast.error(result.detail || 'Failed to improve content');
      }
    } catch (error) {
      console.error('AI content error:', error);
      toast.error('Failed to process document');
    } finally {
      setAiProcessing(false);
    }
  };

  const addToList = (field, value, setter) => {
    if (value.trim() && !formData[field].includes(value.trim())) {
      setFormData({
        ...formData,
        [field]: [...formData[field], value.trim()],
      });
      setter('');
    }
  };

  const removeFromList = (field, index) => {
    setFormData({
      ...formData,
      [field]: formData[field].filter((_, i) => i !== index),
    });
  };

  const filteredSeminars = seminars.filter(seminar => {
    const searchLower = searchTerm.toLowerCase();
    return seminar.title?.toLowerCase().includes(searchLower) ||
           seminar.seminar_code?.toLowerCase().includes(searchLower);
  });

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-700';
      case 'live': return 'bg-red-100 text-red-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-slate-100 text-slate-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)]" data-testid="admin-seminars">
      {/* Left Panel - List */}
      <div className={`${showForm ? 'w-1/2 border-r border-slate-200' : 'w-full'} flex flex-col bg-white`}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="font-heading text-2xl font-bold text-navy-700">Seminar Management</h2>
            <p className="text-slate-500">Create and manage in-depth training seminars</p>
          </div>
          <Button 
            onClick={() => handleOpenForm()}
            className="bg-navy-600 hover:bg-navy-700 rounded-xl"
            data-testid="add-seminar-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Seminar
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex">
            <button
              onClick={() => handleTabChange('active')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'active'
                  ? 'border-navy-600 text-navy-600 bg-navy-50'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
              data-testid="active-seminar-tab"
            >
              Active Seminars
            </button>
            <button
              onClick={() => handleTabChange('inactive')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'inactive'
                  ? 'border-navy-600 text-navy-600 bg-navy-50'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
              data-testid="inactive-seminar-tab"
            >
              Inactive Seminars
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-slate-200 bg-slate-50 space-y-3">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by title or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-lg"
              />
            </div>
          </div>
          
          {/* Filter Row - 4 filters for active, 3 for inactive */}
          <div className={`grid gap-2 ${activeTab === 'active' ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-3'}`}>
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="rounded-lg">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {activeTab === 'active' ? (
                  <>
                    <SelectItem value="all">Status</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="all">Status</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>

            {/* All Seminars Filter - Only for Active Tab */}
            {activeTab === 'active' && (
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="rounded-lg">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Seminars" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Seminars</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="recorded">Recorded</SelectItem>
                  <SelectItem value="certified">Certified</SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* Industry Filter */}
            <SearchableSelect
              options={[
                { value: 'all', label: 'All Industries' },
                ...industries.map((ind) => ({ value: ind.id, label: ind.name }))
              ]}
              value={industryFilter}
              onValueChange={(v) => setIndustryFilter(v || 'all')}
              placeholder="All Industries"
              searchPlaceholder="Search industries..."
              className="w-48 rounded-lg"
              icon={<Filter className="w-4 h-4 shrink-0 text-slate-400" />}
              dataTestId="industry-filter"
              preserveOrder={true}
            />

            {/* Speaker Filter */}
            <SearchableSelect
              options={[
                { value: 'all', label: 'All Speakers' },
                ...speakers.map((sp) => ({ value: sp.id, label: `${sp.first_name} ${sp.last_name}` }))
              ]}
              value={speakerFilter}
              onValueChange={(v) => setSpeakerFilter(v || 'all')}
              placeholder="All Speakers"
              searchPlaceholder="Search speakers..."
              className="w-48 rounded-lg"
              icon={<Filter className="w-4 h-4 shrink-0 text-slate-400" />}
              dataTestId="speaker-filter"
            />
          </div>
        </div>

        {/* Seminars Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
            </div>
          ) : filteredSeminars.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-12 h-12 mx-auto mb-4 text-slate-400" />
              <p className="text-slate-500">No seminars found</p>
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow className="bg-slate-50">
                  <TableHead>Title</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSeminars.map((seminar) => (
                  <TableRow 
                    key={seminar.id} 
                    className={`hover:bg-slate-50 cursor-pointer ${editingSeminar?.id === seminar.id ? 'bg-navy-50' : ''}`}
                    onClick={() => handleOpenForm(seminar)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-10 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 relative">
                          {seminar.thumbnail_url ? (
                            <img src={seminar.thumbnail_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Video className="w-4 h-4 text-slate-400" />
                            </div>
                          )}
                          {seminar.has_cpe_credits && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-gold-500 rounded-full flex items-center justify-center">
                              <Award className="w-2.5 h-2.5 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-navy-700 truncate max-w-[200px]" title={seminar.title}>
                            {seminar.title}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {seminar.seminar_code}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-600">{seminar.sub_industry_name || seminar.industry_name || 'General'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        <span>{seminar.duration_hours}h</span>
                        {seminar.is_multi_day && (
                          <Badge className="bg-purple-100 text-purple-700">{seminar.number_of_days} days</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {seminar.scheduled_date ? (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span>{formatET(seminar.scheduled_date, 'MMM dd, yyyy')}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">Not scheduled</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeColor(seminar.status)}>
                        {seminar.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{seminar.price}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        {seminar.is_featured && (
                          <Star className="w-4 h-4 fill-gold-500 text-gold-500" />
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={(e) => { e.stopPropagation(); handleOpenForm(seminar); }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-600 hover:text-red-700"
                          onClick={(e) => { e.stopPropagation(); handleDeleteSeminar(seminar); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Right Panel - Form (Half Page Side Panel) */}
      {showForm && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 z-40"
            onClick={handleCloseForm}
          />
          <div className="fixed inset-y-0 right-0 w-full md:w-1/2 lg:w-2/5 z-50 bg-white shadow-2xl overflow-auto border-l border-slate-200">
            {/* Form Header - Sticky */}
            <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-slate-200 bg-white shadow-sm">
              <div>
                <h3 className="font-heading text-lg font-semibold text-navy-700">
                  {editingSeminar ? 'Edit Seminar' : 'Create New Seminar'}
                </h3>
                {editingSeminar?.seminar_code && (
                  <Badge className="bg-blue-100 text-blue-700 text-xs mt-1">
                    {editingSeminar.seminar_code}
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloseForm}
                className="h-8 w-8 p-0"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Form Content */}
            <div className="p-4">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="mb-4 w-full grid grid-cols-4 text-xs">
                  <TabsTrigger value="basic">Basic</TabsTrigger>
                  <TabsTrigger value="schedule">Schedule</TabsTrigger>
                  <TabsTrigger value="pricing">Pricing</TabsTrigger>
                  <TabsTrigger value="content">Content</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Title *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          title: e.target.value,
                          slug: generateSlug(e.target.value)
                        })}
                        placeholder="Enter seminar title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>URL Slug</Label>
                      <Input
                        value={formData.slug}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                        placeholder="seminar-url-slug"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Industry *</Label>
                      <SearchableSelect
                        options={industries.map((industry) => ({ value: industry.id, label: industry.name }))}
                        value={formData.industry_id}
                        onValueChange={(value) => setFormData({ ...formData, industry_id: value })}
                        placeholder="Select industry"
                        searchPlaceholder="Search industries..."
                        dataTestId="industry-select"
                        preserveOrder={true}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Short Description</Label>
                    <Input
                      value={formData.short_description}
                      onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                      placeholder="Brief description for listings"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Full Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Detailed description"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Level</Label>
                      <Select 
                        value={formData.level} 
                        onValueChange={(value) => setFormData({ ...formData, level: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="basic">Basic</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Speaker</Label>
                      <SearchableSelect
                        options={speakers.map((speaker) => ({
                          value: speaker.id,
                          label: `${speaker.full_name || `${speaker.first_name} ${speaker.last_name}`}${speaker.speaker_code ? ` (${speaker.speaker_code})` : ''}`
                        }))}
                        value={formData.speaker_ids[0] || ''}
                        onValueChange={(value) => setFormData({ ...formData, speaker_ids: [value] })}
                        placeholder="Select speaker"
                        searchPlaceholder="Search speakers..."
                        dataTestId="speaker-select"
                      />
                      <p className="text-xs text-slate-500">Speaker's code will be used for seminar code generation</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Thumbnail URL</Label>
                    <Input
                      value={formData.thumbnail_url}
                      onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="is_featured"
                        checked={formData.is_featured}
                        onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                        className="rounded border-slate-300"
                      />
                      <Label htmlFor="is_featured" className="text-sm">Featured</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="has_cpe_credits"
                        checked={formData.has_cpe_credits}
                        onChange={(e) => setFormData({ ...formData, has_cpe_credits: e.target.checked })}
                        className="rounded border-slate-300"
                      />
                      <Label htmlFor="has_cpe_credits" className="text-sm">CPE Credits</Label>
                    </div>
                  </div>

                  {formData.has_cpe_credits && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>CPE Credits</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={formData.cpe_credits}
                          onChange={(e) => setFormData({ ...formData, cpe_credits: e.target.value })}
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input
                          type="checkbox"
                          id="is_certified"
                          checked={formData.is_certified}
                          onChange={(e) => setFormData({ ...formData, is_certified: e.target.checked })}
                          className="rounded border-slate-300"
                        />
                        <Label htmlFor="is_certified" className="text-sm">Certified Badge</Label>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="schedule" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Seminar Type</Label>
                      <Select 
                        value={formData.seminar_type} 
                        onValueChange={(value) => setFormData({ ...formData, seminar_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="live">Live</SelectItem>
                          <SelectItem value="on_demand">On Demand</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select 
                        value={formData.status} 
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="scheduled">Scheduled</SelectItem>
                          <SelectItem value="live">Live</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={formData.scheduled_date}
                        onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Start Time</Label>
                      <Input
                        type="time"
                        value={formData.scheduled_time}
                        onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Duration (hours)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="8"
                        step="0.5"
                        value={formData.duration_hours}
                        onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_multi_day"
                      checked={formData.is_multi_day}
                      onChange={(e) => setFormData({ ...formData, is_multi_day: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    <Label htmlFor="is_multi_day">Multi-day Seminar</Label>
                  </div>

                  {formData.is_multi_day && (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>End Date</Label>
                        <Input
                          type="date"
                          value={formData.end_date}
                          onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Time</Label>
                        <Input
                          type="time"
                          value={formData.end_time}
                          onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Number of Days</Label>
                        <Input
                          type="number"
                          min="1"
                          max="10"
                          value={formData.number_of_days}
                          onChange={(e) => setFormData({ ...formData, number_of_days: e.target.value })}
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Timezone</Label>
                    <Select 
                      value={formData.timezone} 
                      onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                        <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                        <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                        <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                        <SelectItem value="UTC">UTC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>

                <TabsContent value="pricing" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Live Access Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price_live}
                        onChange={(e) => setFormData({ ...formData, price_live: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Recorded Access Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price_recorded}
                        onChange={(e) => setFormData({ ...formData, price_recorded: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Digital Download Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price_digital_download}
                        onChange={(e) => setFormData({ ...formData, price_digital_download: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Group Live Price (up to 20)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price_group_live}
                        onChange={(e) => setFormData({ ...formData, price_group_live: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="content" className="space-y-4">
                  {/* AI Content Improvement Section - Disabled for production (emergentintegrations not available on Hostinger) */}
                  {/* 
                  <div className="bg-slate-50 border border-purple-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      <h4 className="font-medium text-purple-900">AI Content Improvement</h4>
                    </div>
                    <p className="text-sm text-purple-700">
                      Upload a PDF or Word document with seminar content. AI will generate professional descriptions.
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => setContentFile(e.target.files[0])}
                          className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple-100 file:text-purple-700 hover:file:bg-purple-200"
                        />
                      </div>
                      <Button
                        type="button"
                        onClick={handleAiImproveContent}
                        disabled={!contentFile || aiProcessing}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        {aiProcessing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Improve Content
                          </>
                        )}
                      </Button>
                    </div>
                    {contentFile && (
                      <p className="text-xs text-purple-600">Selected: {contentFile.name}</p>
                    )}
                  </div>
                  */}

                  <div className="space-y-2">
                    <Label>Overview</Label>
                    <RichTextEditor
                      key={`overview-${editingSeminar?.id || 'new'}`}
                      value={formData.overview}
                      onChange={(value) => setFormData({ ...formData, overview: value })}
                      placeholder="Detailed overview of the seminar"
                      minHeight="120px"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Why Attend</Label>
                    <RichTextEditor
                      key={`why-${editingSeminar?.id || 'new'}`}
                      value={formData.why_attend}
                      onChange={(value) => setFormData({ ...formData, why_attend: value })}
                      placeholder="Key reasons to attend this seminar"
                      minHeight="100px"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Who Should Attend</Label>
                    <RichTextEditor
                      key={`who-${editingSeminar?.id || 'new'}`}
                      value={formData.who_should_attend}
                      onChange={(value) => setFormData({ ...formData, who_should_attend: value })}
                      placeholder="Target audience description"
                      minHeight="100px"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Areas Covered</Label>
                    <RichTextEditor
                      key={`areas-${editingSeminar?.id || 'new'}`}
                      value={formData.areas_covered_text || (Array.isArray(formData.areas_covered) ? formData.areas_covered.join('\n') : '')}
                      onChange={(value) => setFormData({ ...formData, areas_covered_text: value })}
                      placeholder="List the key areas and topics covered..."
                      minHeight="100px"
                    />
                  </div>
                </TabsContent>
              </Tabs>

              {/* Form Actions - Sticky Footer */}
              <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 mt-6 -mx-4 -mb-4 flex justify-end gap-3">
                <Button variant="outline" onClick={handleCloseForm}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveSeminar}
                  disabled={saving}
                  className="bg-navy-600 hover:bg-navy-700"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingSeminar ? 'Update Seminar' : 'Create Seminar'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
